import * as React from 'react'

import { IconX } from '@tabler/icons-react'

import { Button } from '@/components/sui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@/components/sui/dialog'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/sui/drawer'
import { OutsideField } from '@/components/sui/field'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/sui/tooltip'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { useIsPhoneViewport } from '@/hooks/use-phone-viewport'
import { cn } from '@/lib/utils'

import locales from './locales'

type Surface = 'dialog' | 'drawer'
type Size = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl' | '5xl' | 'full'

const SIZE: Record<Size, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '4xl': 'sm:max-w-4xl',
  '5xl': 'sm:max-w-5xl',
  full: 'sm:max-w-[calc(100%-2rem)] h-[calc(100dvh-2rem)]',
}

// A keyline says "content passes under this pinned region", so it needs both a
// region to pin against and a body that actually scrolls: the popup supplies
// the first through `:has()` (which sees through the form's `display: contents`
// and any delegate), the body the second through `data-scrollable`. A dialog
// that fits draws nothing.
const KEYLINE =
  'has-[[data-slot=dialog-header]:not([data-collapsed])]:**:data-[slot=responsive-dialog-body]:data-scrollable:border-t has-[[data-slot=dialog-footer]]:**:data-[slot=responsive-dialog-body]:data-scrollable:border-b'

const SURFACE = {
  dialog: {
    header: 'px-6 pt-3 pb-3',
    titleGroup: 'gap-2',
    titleGroupStart: 'gap-2',
    titleGroupWithClose: 'pr-6',
    controls: 'mt-3',
    body: 'px-6 py-3',
    footer: 'px-6 pt-3 pb-3',
    close: 'absolute top-4 right-4',
    stretch: '',
  },
  drawer: {
    header: 'px-4 pt-2 pb-2',
    titleGroup: 'items-center gap-1 text-center',
    titleGroupStart: 'gap-1',
    titleGroupWithClose: 'px-7',
    controls: 'mt-2',
    body: 'px-4 py-2',
    footer: 'px-4 pt-2 pb-2',
    close: 'absolute top-3 right-3',
    stretch:
      'landscape:h-[calc(100dvh-1.5rem)] landscape:max-h-[calc(100dvh-1.5rem)]!',
    fill: 'h-[calc(100dvh-1.5rem)] max-h-[calc(100dvh-1.5rem)]!',
  },
} as const

const TABBABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'

const POPUP_CHILD_SLOTS = new Set([
  'responsive-dialog-sheet',
  'dialog-header',
  'responsive-dialog-body',
  'dialog-footer',
  'responsive-dialog-form',
  'dialog-close',
  'drawer-close',
])

interface ResponsiveDialogContextValue {
  surface: Surface
  dismissible: boolean
}

const ResponsiveDialogContext =
  React.createContext<ResponsiveDialogContextValue | null>(null)

const CloseContext = React.createContext(false)

function useResponsiveDialogContext(part: string) {
  const context = React.useContext(ResponsiveDialogContext)
  if (!context) {
    throw new Error(`${part} must be rendered inside <ResponsiveDialog>`)
  }
  return context
}

function useResponsiveDialogSurface(): Surface {
  return useResponsiveDialogContext('useResponsiveDialogSurface').surface
}

interface ResponsiveDialogProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** False blocks Escape, overlay, swipe and the close button on both surfaces. */
  dismissible?: boolean
  /** 'drawer' forces the sheet, for mobile-only surfaces whose desktop side is not a dialog. */
  surface?: 'auto' | 'drawer'
  children: React.ReactNode
}

function ResponsiveDialog({
  open,
  onOpenChange,
  dismissible = true,
  surface = 'auto',
  children,
}: ResponsiveDialogProps) {
  const isPhone = useIsPhoneViewport()
  const isDrawer = surface === 'drawer' || isPhone

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next && !dismissible) return
      onOpenChange?.(next)
    },
    [dismissible, onOpenChange],
  )

  const value = React.useMemo<ResponsiveDialogContextValue>(
    () => ({ surface: isDrawer ? 'drawer' : 'dialog', dismissible }),
    [isDrawer, dismissible],
  )

  if (isDrawer) {
    return (
      <ResponsiveDialogContext.Provider value={value}>
        <Drawer
          open={open}
          onOpenChange={handleOpenChange}
          dismissible={dismissible}
          shouldScaleBackground={false}
        >
          {children}
        </Drawer>
      </ResponsiveDialogContext.Provider>
    )
  }

  // Outside clicks never dismiss on desktop: browser extensions inject
  // synthetic clicks into cross-origin iframes (Stripe checkout) and long
  // forms would be lost by a slip. The X and Escape remain.
  return (
    <ResponsiveDialogContext.Provider value={value}>
      <Dialog
        open={open}
        onOpenChange={handleOpenChange}
        disablePointerDismissal
      >
        {children}
      </Dialog>
    </ResponsiveDialogContext.Provider>
  )
}

interface ResponsiveDialogTriggerProps {
  children: React.ReactNode
  render?: React.ReactElement
}

function ResponsiveDialogTrigger({
  children,
  render,
}: ResponsiveDialogTriggerProps) {
  const { surface } = useResponsiveDialogContext('ResponsiveDialogTrigger')

  if (surface === 'drawer') {
    if (render) {
      return (
        <DrawerTrigger asChild>
          {React.cloneElement(render, {}, children)}
        </DrawerTrigger>
      )
    }
    return <DrawerTrigger>{children}</DrawerTrigger>
  }

  return <DialogTrigger render={render}>{children}</DialogTrigger>
}

interface ResponsiveDialogCloseProps {
  children: React.ReactNode
  render?: React.ReactElement
}

function ResponsiveDialogClose({
  children,
  render,
}: ResponsiveDialogCloseProps) {
  const { surface } = useResponsiveDialogContext('ResponsiveDialogClose')

  if (surface === 'drawer') {
    if (render) {
      return (
        <DrawerClose asChild>
          {React.cloneElement(render, {}, children)}
        </DrawerClose>
      )
    }
    return <DrawerClose>{children}</DrawerClose>
  }

  return <DialogClose render={render}>{children}</DialogClose>
}

function CloseButton({ surface }: { surface: Surface }) {
  const { t } = useComponentTranslation(locales)

  return (
    <Tooltip>
      <ResponsiveDialogClose
        render={
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="icon-sm"
                className={SURFACE[surface].close}
              />
            }
          />
        }
      >
        <IconX />
        <span className="sr-only">{t('close')}</span>
      </ResponsiveDialogClose>
      <TooltipContent>{t('close')}</TooltipContent>
    </Tooltip>
  )
}

function warnOnLooseChildren(popup: HTMLElement, surface: Surface) {
  // On the sheet the regions sit inside the scroller, so that is where the
  // caller's children actually land.
  const host =
    popup.querySelector('[data-slot="responsive-dialog-sheet"]') ?? popup
  const children = Array.from(host.children).slice(
    host === popup && surface === 'drawer' ? 1 : 0,
  )
  for (const child of children) {
    const slot = child.getAttribute('data-slot') ?? ''
    if (POPUP_CHILD_SLOTS.has(slot)) continue
    console.warn(
      `[ResponsiveDialog] <${child.tagName.toLowerCase()}> is a loose child of ResponsiveDialogContent; wrap it in ResponsiveDialogBody.`,
    )
  }
}

interface ResponsiveDialogContentProps {
  children: React.ReactNode
  size?: Size
  showCloseButton?: boolean
  /** Drawing surfaces take the whole sheet on landscape phones; 'phone' takes it in every orientation. */
  stretch?: 'phone-landscape' | 'phone'
  flush?: boolean
}

function ResponsiveDialogContent({
  children,
  size = 'md',
  showCloseButton = true,
  stretch,
  flush = false,
}: ResponsiveDialogContentProps) {
  const { surface, dismissible } = useResponsiveDialogContext(
    'ResponsiveDialogContent',
  )
  const popupRef = React.useRef<HTMLDivElement>(null)
  // A sheet is not a modal: it already closes by swipe, by the overlay and by
  // its own footer action, so it never draws an X.
  const hasClose = showCloseButton && dismissible && surface === 'dialog'

  // The popup mounts in a portal a commit later than Content, so the check
  // rides on the ref attaching rather than on an effect.
  const setPopup = React.useCallback(
    (node: HTMLDivElement | null) => {
      popupRef.current = node
      if (node && import.meta.env.DEV) warnOnLooseChildren(node, surface)
    },
    [surface],
  )

  const inner = (
    <>
      <CloseContext.Provider value={hasClose}>
        <OutsideField>{children}</OutsideField>
      </CloseContext.Provider>
      {hasClose && <CloseButton surface={surface} />}
    </>
  )

  if (surface === 'drawer') {
    return (
      <DrawerContent
        ref={setPopup}
        // vaul cancels the "focus first tabbable" default but leaves focus on
        // the trigger, now inside an aria-hidden subtree. Move it to the panel.
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          popupRef.current?.focus({ preventScroll: true })
        }}
        className={cn(
          stretch === 'phone-landscape' && SURFACE.drawer.stretch,
          stretch === 'phone' && SURFACE.drawer.fill,
        )}
      >
        {/* vaul drags the role="dialog" surface to dismiss and only yields to a
            scrollable CHILD, so the sheet's one scroller lives in here. */}
        <div
          data-slot="responsive-dialog-sheet"
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain has-[>[data-layout=fill]]:flex has-[>[data-layout=fill]]:flex-col"
        >
          <CloseContext.Provider value={hasClose}>
            <OutsideField>{children}</OutsideField>
          </CloseContext.Provider>
        </div>
      </DrawerContent>
    )
  }

  return (
    <DialogContent
      ref={setPopup}
      className={cn(SIZE[size], flush && 'py-0', KEYLINE)}
      showCloseButton={false}
      initialFocus={() => popupRef.current}
    >
      {inner}
    </DialogContent>
  )
}

interface ResponsiveDialogHeaderProps {
  children: React.ReactNode
  /** Keeps the accessible name while hiding the title bar; 'phone-landscape' only on the sheet in landscape. */
  visuallyHidden?: true | 'phone-landscape'
  /** Full-width controls that must stay pinned with the title (a picker's search field and tabs). */
  controls?: React.ReactNode
  /** 'start' left-aligns the sheet's title group, for a header that mirrors a card's; the dialog already is. */
  align?: 'start'
}

function ResponsiveDialogHeader({
  children,
  visuallyHidden,
  controls,
  align,
}: ResponsiveDialogHeaderProps) {
  const { surface } = useResponsiveDialogContext('ResponsiveDialogHeader')
  const hasClose = React.useContext(CloseContext)
  const tokens = SURFACE[surface]
  const landscapeOnly = visuallyHidden === 'phone-landscape'
  const hidden =
    visuallyHidden === true
      ? 'sr-only'
      : landscapeOnly && surface === 'drawer'
        ? 'landscape:sr-only'
        : undefined

  // With no close button to host, a hidden header collapses to nothing — and a
  // keyline under nothing is the empty band this primitive exists to remove.
  const collapsed = visuallyHidden === true && !hasClose

  return (
    <div
      data-slot="dialog-header"
      data-collapsed={collapsed || undefined}
      className={cn('shrink-0', tokens.header, hidden && !hasClose && hidden)}
    >
      {hidden && hasClose && (
        <div
          aria-hidden
          className={cn('h-8', landscapeOnly && 'hidden landscape:block')}
        />
      )}
      <div
        className={cn(
          'flex flex-col',
          align === 'start' ? tokens.titleGroupStart : tokens.titleGroup,
          hasClose && tokens.titleGroupWithClose,
          hasClose && hidden,
        )}
      >
        {children}
      </div>
      {controls && <div className={tokens.controls}>{controls}</div>}
    </div>
  )
}

interface ResponsiveDialogBodyProps {
  children: React.ReactNode
  /** 'centered' centres the content vertically when the popup has slack; 'split' hands scrolling to the children (two-pane bodies); 'fill' also does so inside a sheet, taking its whole height. */
  layout?: 'centered' | 'split' | 'fill'
  /** Edge-to-edge content that owns its own padding. */
  flush?: boolean
}

function ResponsiveDialogBody({
  children,
  layout,
  flush,
}: ResponsiveDialogBodyProps) {
  const { surface } = useResponsiveDialogContext('ResponsiveDialogBody')
  const { t } = useComponentTranslation(locales)
  const ref = React.useRef<HTMLDivElement>(null)
  const [state, setState] = React.useState({
    scrollable: false,
    isRegion: false,
  })

  // One measurement serves both consumers: the keyline, which only means
  // anything while content passes under a pinned region, and the keyboard
  // reachability of a long read-only body. Taken on mount and whenever the
  // scroller or its content resizes — never per render, so typing in a form
  // forces no reflow.
  const measure = React.useCallback(() => {
    const element = ref.current
    if (!element) return
    const scrollable = element.scrollHeight > element.clientHeight
    const next = {
      scrollable,
      isRegion: scrollable && !element.querySelector(TABBABLE),
    }
    setState((previous) =>
      previous.scrollable === next.scrollable &&
      previous.isRegion === next.isRegion
        ? previous
        : next,
    )
  }, [])

  React.useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    // The content box is what grows when a field is added or a section opens;
    // the scroller's own box may never change.
    if (element.firstElementChild) observer.observe(element.firstElementChild)
    return () => observer.disconnect()
  }, [measure])

  return (
    <div
      ref={ref}
      data-slot="responsive-dialog-body"
      className={cn(
        surface === 'drawer'
          ? layout === 'fill'
            ? 'flex min-h-0 flex-1 flex-col overflow-hidden'
            : ''
          : layout === 'split' || layout === 'fill'
            ? 'flex min-h-0 flex-1 flex-col overflow-hidden'
            : 'min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain',
        layout === 'centered' && 'flex flex-col',
        flush ? 'p-0' : SURFACE[surface].body,
        'focus-visible:ring-ring/50 outline-none focus-visible:ring-2 focus-visible:ring-inset',
      )}
      data-layout={layout === 'fill' ? layout : undefined}
      data-scrollable={state.scrollable || undefined}
      tabIndex={state.isRegion ? 0 : -1}
      role={state.isRegion ? 'region' : undefined}
      aria-label={state.isRegion ? t('scrollableContent') : undefined}
    >
      {layout === 'centered' ? (
        <div className="m-auto w-full">{children}</div>
      ) : (
        children
      )}
    </div>
  )
}

interface ResponsiveDialogFooterProps {
  children: React.ReactNode
  /** One column, primary action first. */
  layout?: 'stacked'
}

function ResponsiveDialogFooter({
  children,
  layout,
}: ResponsiveDialogFooterProps) {
  const { surface } = useResponsiveDialogContext('ResponsiveDialogFooter')

  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'grid shrink-0 grid-cols-[repeat(auto-fit,minmax(0,1fr))] gap-2',
        layout === 'stacked' && 'grid-cols-1',
        SURFACE[surface].footer,
      )}
    >
      {children}
    </div>
  )
}

type ResponsiveDialogFormProps = Omit<React.ComponentProps<'form'>, 'className'>

/**
 * Boxless form: Body and Footer stay flex items of the popup while the submit
 * button is an ordinary nested `type="submit"`.
 */
function ResponsiveDialogForm({
  onSubmit,
  noValidate = true,
  ...props
}: ResponsiveDialogFormProps) {
  return (
    <form
      data-slot="responsive-dialog-form"
      className="contents"
      noValidate={noValidate}
      // Pages that wrap themselves in a <form> (white-label shell, SSO) would
      // receive this submit through React's tree, portal or not.
      onSubmit={(event) => {
        event.stopPropagation()
        onSubmit?.(event)
      }}
      {...props}
    />
  )
}

interface ResponsiveDialogHeroProps {
  icon?: React.ReactNode
  children: React.ReactNode
}

/** Centred icon + Title + Description for dialogs without a header. */
function ResponsiveDialogHero({ icon, children }: ResponsiveDialogHeroProps) {
  return (
    <div
      data-slot="responsive-dialog-hero"
      className="flex flex-col items-center gap-2 text-center"
    >
      {icon}
      {children}
    </div>
  )
}

interface ResponsiveDialogTitleProps {
  children: React.ReactNode
  icon?: React.ReactNode
  className?: string
}

function ResponsiveDialogTitle({
  children,
  icon,
  className,
}: ResponsiveDialogTitleProps) {
  const { surface } = useResponsiveDialogContext('ResponsiveDialogTitle')
  const Title = surface === 'drawer' ? DrawerTitle : DialogTitle

  if (!icon) return <Title className={className}>{children}</Title>

  return (
    <Title className={cn('inline-flex items-center gap-2', className)}>
      <span aria-hidden className="shrink-0 [&_svg]:size-5">
        {icon}
      </span>
      <span>{children}</span>
    </Title>
  )
}

interface ResponsiveDialogDescriptionProps {
  children: React.ReactNode
  className?: string
}

function ResponsiveDialogDescription({
  children,
  className,
}: ResponsiveDialogDescriptionProps) {
  const { surface } = useResponsiveDialogContext('ResponsiveDialogDescription')
  const Description =
    surface === 'drawer' ? DrawerDescription : DialogDescription

  return <Description className={className}>{children}</Description>
}

interface ResponsiveDialogDismissProps extends Omit<
  React.ComponentProps<typeof Button>,
  'variant' | 'type' | 'children'
> {
  /** cancel: outline "Cancel"; close: outline "Close"; acknowledge: primary "Got it". */
  intent: 'cancel' | 'close' | 'acknowledge'
  /** Only for a real alternative choice ("Keep subscription"); the intent's label otherwise. */
  children?: React.ReactNode
}

function ResponsiveDialogDismiss({
  intent,
  children,
  ...props
}: ResponsiveDialogDismissProps) {
  const { t } = useComponentTranslation(locales)

  return (
    <ResponsiveDialogClose
      render={
        <Button
          type="button"
          variant={intent === 'acknowledge' ? 'default' : 'outline'}
          {...props}
        />
      }
    >
      {children ?? t(intent)}
    </ResponsiveDialogClose>
  )
}

export {
  ResponsiveDialog,
  ResponsiveDialogBody,
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogDismiss,
  ResponsiveDialogFooter,
  ResponsiveDialogForm,
  ResponsiveDialogHeader,
  ResponsiveDialogHero,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
  useResponsiveDialogSurface,
}
